import Image from '@tiptap/extension-image';
import { TextStyleKit } from '@tiptap/extension-text-style';
import { Fragment, Slice } from '@tiptap/pm/model';
import {
  EditorContent, useEditor, useEditorState,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import cx from 'classnames';
import React from 'react';
import { toast } from 'sonner';

import {
  ButtonBase,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import CustomInputDashboard from 'creative-components/CustomInput/CustomInputDashboard';

import ActionModal from 'components/ActionModal/ActionModal';

import { getPlainTextPasteSegments } from './plainTextPaste';

const hasHtmlTags = (value) => /<\/?[a-z][\s\S]*>/i.test(value || '');

const escapeHtml = (value) => (value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

/**
 * Normalizes the rich text value and applies various fixes, like:
 * 1. Converting empty paragraph tags (line breaks) to safe spacers for use in emails
 */
const normalizeRichTextValue = (value) => {
  if (!value) return '<p></p>';

  // If this has html tags then its already rich text
  if (hasHtmlTags(value)) return value;

  // Preserve newlines inside one paragraph
  const escaped = escapeHtml(value).replace(/\r?\n/g, '<br />');
  return `<p>${escaped}</p>`;
};

const stripRichText = (value) => (value || '')
  .replace(/<[^>]*>/g, '')
  .replace(/&nbsp;/g, ' ')
  .trim();

/**
 * ProseMirror's default plain-text clipboard parser splits on one or more
 * newlines, which collapses blank lines. Build an inline slice instead so
 * every pasted newline becomes an explicit hard break.
 */
const createPlainTextPasteSlice = (text, schema, marks = []) => {
  const content = getPlainTextPasteSegments(text).map((segment) => (
    segment.type === 'hardBreak'
      ? schema.nodes.hardBreak.create(null, null, marks)
      : schema.text(segment.text, marks)
  ));

  return new Slice(Fragment.fromArray(content), 0, 0);
};

const DEFAULT_TEXT_COLOR = '#1f2937';
const MAX_IMAGE_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const DEFAULT_FONT_SIZE_PX = 14;
const FONT_SIZE_STEPS = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72];

const parseFontSizePx = (value) => {
  if (typeof value !== 'string') return null;

  const parsedValue = Number.parseFloat(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const getNextFontSize = (currentValue, direction) => {
  const currentFontSize = parseFontSizePx(currentValue) ?? DEFAULT_FONT_SIZE_PX;

  if (direction > 0) {
    const nextFontSize = FONT_SIZE_STEPS.find((fontSize) => fontSize > currentFontSize);
    return `${nextFontSize ?? FONT_SIZE_STEPS[FONT_SIZE_STEPS.length - 1]}px`;
  }

  const previousFontSizes = [...FONT_SIZE_STEPS].reverse();
  const previousFontSize = previousFontSizes.find((fontSize) => fontSize < currentFontSize);
  return `${previousFontSize ?? FONT_SIZE_STEPS[0]}px`;
};

const TokenizedImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      customImageToken: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-custom-image-token'),
        renderHTML: (attributes) => {
          if (!attributes.customImageToken) return {};
          return { 'data-custom-image-token': attributes.customImageToken };
        },
      },
    };
  },
});

const useStyles = makeStyles((theme) => ({
  richTextWrapper: {
    marginTop: '16px',
  },
  richTextLabel: {
    color: theme.palette.darkGray.main,
    fontFamily: 'Montserrat',
    fontWeight: 500,
    marginBottom: '8px',
  },
  richTextToolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '10px',
  },
  richTextButton: {
    borderRadius: '6px',
    padding: '4px 10px',
    fontWeight: 600,
    fontSize: '12px',
    border: `1px solid ${theme.palette.grayScale5.main}`,
    background: theme.palette.white.main,
    minWidth: '34px',
    minHeight: '30px',
  },
  colorPickerButton: {
    position: 'relative',
    overflow: 'hidden',
  },
  richTextButtonActive: {
    border: `1px solid ${theme.palette.primary.light}`,
    background: theme.palette.primary.light,
  },
  richTextDivider: {
    width: '1px',
    background: theme.palette.grayScale5.main,
    margin: '0 4px',
  },
  richTextSymbol: {
    fontFamily: 'Material Symbols Outlined',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '18px',
    lineHeight: 1,
    letterSpacing: 'normal',
    textTransform: 'none',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    wordWrap: 'normal',
    direction: 'ltr',
    fontFeatureSettings: 'liga',
    fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20",
  },
  colorInput: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    border: 0,
    padding: 0,
    opacity: 0,
    cursor: 'pointer',
  },
  colorSwatch: {
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    border: `1px solid ${theme.palette.grayScale5.main}`,
    marginLeft: '6px',
    display: 'inline-block',
  },
  hiddenFileInput: {
    display: 'none',
  },
  richTextEditor: {
    borderRadius: '8px',
    border: `2px solid ${theme.palette.grayScale5.main}`,
    padding: '10px 12px',
    minHeight: '180px',
    background: theme.palette.white.main,
    display: 'flex',
    flexDirection: 'column',
    resize: 'vertical',
    overflow: 'auto',
    '& > div': {
      flex: 1,
      display: 'flex',
      minHeight: 0,
    },
  },
  richTextEditorFocused: {
    borderColor: theme.palette.primary.main,
    background: '#F9FDFE',
  },
  richTextEditorError: {
    borderColor: theme.palette.error.main,
    background: '#FEFAFA',
  },
  richTextContent: {
    flex: 1,
    minHeight: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'Montserrat',
    lineHeight: '20px',
    color: theme.palette.darkGray.main,
    '& p': {
      fontSize: '14px',
    },
    '& p, & h1, & h2, & h3, & h4, & h5, & h6, & ul, & ol, & blockquote, & hr': {

    },
    '& ul, & ol': {
      paddingLeft: '24px',
    },
    '& li': {
      margin: 0,
    },
    '& li + li': {
      marginTop: '4px',
    },
    '& > *:first-child': {
      marginTop: 0,
    },
    '& > *:last-child': {
      marginBottom: 0,
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      color: theme.palette.darkGray.main,
    },
    '& a': {
      color: '-webkit-link',
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    '& strong, & b': {
      fontWeight: 700,
    },
    '& img': {

    },

    /* Image resize handles */
    '& [data-resize-container]': {
      display: 'inline-block !important',
      maxWidth: '100%',
    },
    '& .ProseMirror-selectednode': {
      '& [data-resize-wrapper]': {
        position: 'relative',
        display: 'inline-block',
        maxWidth: '100%',
        outline: `2px solid ${theme.palette.primary.light}`,
      },
      '& [data-resize-handle]': {
        width: '10px',
        height: '10px',
        background: theme.palette.primary.main,
        border: `1px solid ${theme.palette.white.main}`,
        borderRadius: '2px',
        zIndex: 2,
      },
      '& [data-resize-handle="top-left"], & [data-resize-handle="bottom-right"]': {
        cursor: 'nwse-resize',
      },
      '& [data-resize-handle="top-right"], & [data-resize-handle="bottom-left"]': {
        cursor: 'nesw-resize',
      },

    },
  /* Image resize handles */
  },
  flash: { // Used to do a flash when AI updates text
    animation: '$flashHighlight 3000ms ease-in-out',
  },
  '@keyframes flashHighlight': {
    '0%': { backgroundColor: '#fff3cd' }, // Light yellow
    '40%': { backgroundColor: '#fff3cd' }, // Light yellow
    '100%': { backgroundColor: 'transparent' },
  },
}));

const generateImageToken = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `img_${crypto.randomUUID()}`;
  }

  return `img_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const getCustomImageTokensFromHtml = (html) => {
  if (!html || typeof document === 'undefined') return [];

  const container = document.createElement('div');
  container.innerHTML = html;

  return Array.from(container.querySelectorAll('img[data-custom-image-token]'))
    .map((image) => image.getAttribute('data-custom-image-token'))
    .filter(Boolean);
};

const TiptapEditor = ({
  value,
  onChange,
  onBlur,
  error,
  flash,
  onEditorReady,
  onSubmitPayloadReady,
  hideLinkButton = false,
}) => {
  const classes = useStyles();
  const [isFocused, setIsFocused] = React.useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = React.useState(false);
  const [linkDraft, setLinkDraft] = React.useState('');
  const [linkSelection, setLinkSelection] = React.useState(null);
  const [imageInsertSelection, setImageInsertSelection] = React.useState(null);
  const [managedImages, setManagedImages] = React.useState({});
  const managedImagesRef = React.useRef({});
  const imageInputRef = React.useRef(null);
  const lastEditorHtmlRef = React.useRef(null);

  const editor = useEditor({
    coreExtensionOptions: {
      clipboardTextSerializer: {
        // Match the editor's one-line block transitions when copying as plain text.
        blockSeparator: '\n',
      },
    },
    extensions: [
      StarterKit,
      TextStyleKit,
      TokenizedImage.configure({
        inline: true,
        resize: {
          enabled: true,
          alwaysPreserveAspectRatio: true,
          minHeight: 50,
          minWidth: 50,
        },
      }),
    ],
    content: normalizeRichTextValue(value),
    onUpdate: ({ editor: activeEditor }) => {
      const html = activeEditor.getHTML();
      lastEditorHtmlRef.current = html;
      onChange(html);

      // NOTE: Not needed, but maybe add later if there are issues with not cleaning stuff up?
      /* const activeTokens = new Set(getCustomImageTokensFromHtml(html));
      setManagedImages((previousImages) => {
        const nextImages = { ...previousImages };
        let hasChanges = false;

        Object.entries(previousImages).forEach(([token, image]) => {
          if (!activeTokens.has(token)) {
            URL.revokeObjectURL(image.blobUrl);
            delete nextImages[token];
            hasChanges = true;
          }
        });

        return hasChanges ? nextImages : previousImages;
      }); */
    },
    editorProps: {
      attributes: {
        class: classes.richTextContent,
      },
      clipboardTextParser: (text, $context, plain, view) => (
        createPlainTextPasteSlice(text, view.state.schema, $context.marks())
      ),
    },
  });

  React.useEffect(() => {
    managedImagesRef.current = managedImages;
  }, [managedImages]);

  React.useEffect(() => () => {
    Object.values(managedImagesRef.current).forEach((image) => {
      URL.revokeObjectURL(image.blobUrl);
    });
  }, []);

  React.useEffect(() => {
    if (!editor) return;
    onEditorReady(editor);
  }, [editor, onEditorReady]);

  // Sync external value updates (e.g. AI autofill) into the editor, but avoid echo loops from onUpdate.
  React.useEffect(() => {
    if (!editor) return;

    const nextValue = normalizeRichTextValue(value);
    if (editor.getHTML() === nextValue) return;
    if (lastEditorHtmlRef.current === nextValue) return;

    editor.commands.setContent(nextValue, false);
    lastEditorHtmlRef.current = nextValue;
  }, [editor, value]);

  React.useEffect(() => {
    if (!editor) return undefined;

    const handleBlur = () => {
      setIsFocused(false);
      onBlur();
    };
    const handleFocus = () => setIsFocused(true);

    editor.on('blur', handleBlur);
    editor.on('focus', handleFocus);

    return () => {
      editor.off('blur', handleBlur);
      editor.off('focus', handleFocus);
    };
  }, [editor, onBlur]);

  const keepEditorSelection = (event) => {
    event.preventDefault();
  };

  const toolbarState = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => {
      const imageAttrs = currentEditor.getAttributes('image') || {};
      const widthFromStyle = typeof imageAttrs.style === 'string'
        ? imageAttrs.style.match(/width:\s*([^;]+)/i)?.[1] || null
        : null;
      const imageWidth = imageAttrs.width || widthFromStyle;

      return {
        canEdit: currentEditor.isEditable,

        // Text formatting
        isBold: currentEditor.isActive('bold') ?? false,
        canBold: currentEditor.can().chain().toggleBold().run() ?? false,
        isItalic: currentEditor.isActive('italic') ?? false,
        canItalic: currentEditor.can().chain().toggleItalic().run() ?? false,
        isStrike: currentEditor.isActive('strike') ?? false,
        canStrike: currentEditor.can().chain().toggleStrike().run() ?? false,
        isUnderline: currentEditor.isActive('underline') ?? false,
        canUnderline: currentEditor.can().chain().toggleUnderline().run() ?? false,
        isLink: currentEditor.isActive('link') ?? false,
        selectedTextColor: currentEditor.getAttributes('textStyle')?.color || DEFAULT_TEXT_COLOR,
        selectedFontSize: currentEditor.getAttributes('textStyle')?.fontSize || null,

        // Block types
        isParagraph: currentEditor.isActive('paragraph') ?? false,
        headingLevels: [1, 2, 3, 4, 5, 6].reduce((acc, level) => {
          acc[level] = currentEditor.isActive('heading', { level });
          return acc;
        }, {}),

        // Lists and blocks
        isBulletList: currentEditor.isActive('bulletList') ?? false,
        isOrderedList: currentEditor.isActive('orderedList') ?? false,

        isImage: currentEditor.isActive('image') ?? false,
        imageWidth,
      };
    },
  });

  const openLinkModal = () => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    setLinkSelection({ from, to });
    setLinkDraft(editor.getAttributes('link')?.href || '');
    setIsLinkModalOpen(true);
  };

  const applyLinkFromModal = () => {
    if (!editor) return;

    const href = linkDraft.trim();
    const command = editor.chain().focus();

    if (linkSelection) {
      command.setTextSelection(linkSelection);
    }

    if (!href) {
      command.unsetLink().run();
    } else {
      command.extendMarkRange('link').setLink({ href }).run();
    }

    setIsLinkModalOpen(false);
  };

  const onInsertImageClick = () => {
    if (!editor) return;

    const { from, to } = editor.state.selection;

    // If image node is selected, collapse selection after it so insert won't overwrite.
    if (editor.isActive('image')) {
      setImageInsertSelection({ from: to, to });
    } else {
      setImageInsertSelection({ from, to });
    }

    imageInputRef.current?.click();
  };

  const adjustFontSize = (direction) => {
    if (!editor) return;

    const nextFontSize = getNextFontSize(toolbarState.selectedFontSize, direction);
    editor.chain().focus().setFontSize(nextFontSize).run();
  };

  const onImageFileSelected = (event) => {
    const inputElement = event.currentTarget;
    const file = inputElement.files?.[0];
    inputElement.value = '';

    if (!file || !editor) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }

    if (file.size > MAX_IMAGE_FILE_SIZE) {
      toast.error('Image is too large. Max size is 10MB.');
      return;
    }

    const token = generateImageToken();
    const blobUrl = URL.createObjectURL(file);

    setManagedImages((previousImages) => ({
      ...previousImages,
      [token]: { token, file, blobUrl },
    }));

    const command = editor.chain().focus();
    if (imageInsertSelection) {
      command.setTextSelection({ from: imageInsertSelection.from, to: imageInsertSelection.to });
    }

    command.setImage({
      src: blobUrl,
      alt: file.name,
      customImageToken: token,
    }).run();

    setImageInsertSelection(null);
  };

  const buildSubmitPayload = React.useCallback(() => {
    if (!editor || typeof document === 'undefined') {
      return {
        htmlForSubmit: normalizeRichTextValue(value),
        customBodyImageMap: {},
        customBodyImages: [],
      };
    }

    const container = document.createElement('div');
    container.innerHTML = editor.getHTML();

    const customBodyImageMap = {};
    const customBodyImages = [];

    container.querySelectorAll('img').forEach((imageNode) => {
      const token = imageNode.getAttribute('data-custom-image-token');
      if (!token) return;

      const managedImage = managedImagesRef.current[token];
      if (!managedImage) return;

      const fileIndex = customBodyImages.length;
      customBodyImages.push(managedImage.file);
      customBodyImageMap[token] = fileIndex;

      imageNode.setAttribute('src', `{{customImage:${token}}}`);
      imageNode.removeAttribute('data-custom-image-token');
    });

    return {
      htmlForSubmit: container.innerHTML,
      customBodyImageMap,
      customBodyImages,
    };
  }, [editor, value]);

  React.useEffect(() => {
    if (!onSubmitPayloadReady) return undefined;

    onSubmitPayloadReady(buildSubmitPayload);
    return () => {
      onSubmitPayloadReady(null);
    };
  }, [buildSubmitPayload, onSubmitPayloadReady]);

  return (
    <div className={classes.richTextWrapper}>
      {!hideLinkButton && isLinkModalOpen && (
        <ActionModal
          title="Edit Hyperlink"
          onClose={() => setIsLinkModalOpen(false)}
          backgroundColor="#ffffff"
          textColor="#1f2937"
          maxWidth="md"
          message={(
            <div style={{ width: '500px', maxWidth: '100%' }}>
              <CustomInputDashboard
                labelText="URL"
                inputProps={{
                  autoFocus: true,
                  value: linkDraft,
                  onChange: (event) => setLinkDraft(event.target.value),
                  placeholder: 'https://example.com',
                }}
              />
            </div>
          )}
          buttons={[
            <ButtonBase onClick={() => setIsLinkModalOpen(false)}>Cancel</ButtonBase>,
            <ButtonBase onClick={applyLinkFromModal}>Save</ButtonBase>,
          ]}
        />
      )}

      <div className={classes.richTextLabel}>Body Text</div>
      <div className={classes.richTextToolbar}>
        <ButtonBase
          className={cx(classes.richTextButton, toolbarState.isBold && classes.richTextButtonActive)}
          onMouseDown={keepEditorSelection}
          onClick={() => editor?.chain().focus().toggleBold().run()}
          disabled={!toolbarState.canBold}
          aria-label="Toggle bold"
        >
          <span className={classes.richTextSymbol}>format_bold</span>
        </ButtonBase>
        <ButtonBase
          className={cx(classes.richTextButton, toolbarState.isItalic && classes.richTextButtonActive)}
          onMouseDown={keepEditorSelection}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          disabled={!toolbarState.canItalic}
          aria-label="Toggle italic"
        >
          <span className={classes.richTextSymbol}>format_italic</span>
        </ButtonBase>
        <ButtonBase
          className={cx(classes.richTextButton, toolbarState.isUnderline && classes.richTextButtonActive)}
          onMouseDown={keepEditorSelection}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
          disabled={!toolbarState.canUnderline}
          aria-label="Toggle underline"
        >
          <span className={classes.richTextSymbol}>format_underlined</span>
        </ButtonBase>
        <ButtonBase
          className={cx(classes.richTextButton, toolbarState.isStrike && classes.richTextButtonActive)}
          onMouseDown={keepEditorSelection}
          onClick={() => editor?.chain().focus().toggleStrike().run()}
          disabled={!toolbarState.canEdit}
          aria-label="Toggle strikethrough"
        >
          <span className={classes.richTextSymbol}>format_strikethrough</span>
        </ButtonBase>
        {!hideLinkButton && (
          <ButtonBase
            className={cx(classes.richTextButton, toolbarState.isLink && classes.richTextButtonActive)}
            onMouseDown={keepEditorSelection}
            onClick={openLinkModal}
            disabled={!toolbarState.canEdit}
            aria-label="Set hyperlink"
          >
            <span className={classes.richTextSymbol}>link</span>
          </ButtonBase>
        )}

        <ButtonBase
          className={classes.richTextButton}
          onMouseDown={keepEditorSelection}
          onClick={onInsertImageClick}
          disabled={!toolbarState.canEdit}
          aria-label="Insert image"
        >
          <span className={classes.richTextSymbol}>image</span>
        </ButtonBase>
        <input
          ref={imageInputRef}
          className={classes.hiddenFileInput}
          type="file"
          accept="image/*"
          onChange={onImageFileSelected}
          aria-label="Select image file"
        />

        <span className={classes.richTextDivider} />
        <ButtonBase
          className={classes.richTextButton}
          onMouseDown={keepEditorSelection}
          onClick={() => adjustFontSize(-1)}
          disabled={!toolbarState.canEdit}
          aria-label="Decrease font size"
        >
          <span className={classes.richTextSymbol}>text_decrease</span>
        </ButtonBase>
        <ButtonBase
          className={classes.richTextButton}
          onMouseDown={keepEditorSelection}
          onClick={() => adjustFontSize(1)}
          disabled={!toolbarState.canEdit}
          aria-label="Increase font size"
        >
          <span className={classes.richTextSymbol}>text_increase</span>
        </ButtonBase>
        {[1, 2, 3, 4, 5, 6].map((level) => (
          <ButtonBase
            key={`heading-${level}`}
            className={cx(classes.richTextButton, toolbarState.headingLevels[level] && classes.richTextButtonActive)}
            onMouseDown={keepEditorSelection}
            onClick={() => editor?.chain().focus().toggleHeading({ level }).run()}
            disabled={!toolbarState.canEdit}
            aria-label={`Toggle heading ${level}`}
          >
            <span className={classes.richTextSymbol}>{`format_h${level}`}</span>
          </ButtonBase>
        ))}
        <ButtonBase
          className={classes.richTextButton}
          onMouseDown={keepEditorSelection}
          onClick={() => editor?.chain().focus().setHorizontalRule().run()}
          disabled={!toolbarState.canEdit}
          aria-label="Insert horizontal rule"
        >
          <span className={classes.richTextSymbol}>horizontal_rule</span>
        </ButtonBase>
        <span className={classes.richTextDivider} />
        <ButtonBase
          className={cx(classes.richTextButton, classes.colorPickerButton, toolbarState.isTextStyle && classes.richTextButtonActive)}
          disabled={!toolbarState.canEdit}
          aria-label="Open text color picker"
        >
          <span className={classes.richTextSymbol}>format_color_text</span>
          <span
            className={classes.colorSwatch}
            style={{ backgroundColor: toolbarState.selectedTextColor }}
          />
          <input
            className={classes.colorInput}
            type="color"
            value={toolbarState.selectedTextColor}
            onChange={(event) => {
              const color = event.target.value;
              editor?.chain().focus().setColor(color).run();
            }}
            aria-label="Pick text color"
          />
        </ButtonBase>
        <ButtonBase
          className={classes.richTextButton}
          onMouseDown={keepEditorSelection}
          onClick={() => editor?.chain().focus().unsetColor().run()}
          disabled={!toolbarState.canEdit}
          aria-label="Clear text color"
        >
          <span className={classes.richTextSymbol}>format_color_reset</span>
        </ButtonBase>
        <ButtonBase
          className={cx(classes.richTextButton, toolbarState.isBulletList && classes.richTextButtonActive)}
          onMouseDown={keepEditorSelection}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          disabled={!toolbarState.canEdit}
          aria-label="Toggle bulleted list"
        >
          <span className={classes.richTextSymbol}>format_list_bulleted</span>
        </ButtonBase>
        <ButtonBase
          className={cx(classes.richTextButton, toolbarState.isOrderedList && classes.richTextButtonActive)}
          onMouseDown={keepEditorSelection}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          disabled={!toolbarState.canEdit}
          aria-label="Toggle numbered list"
        >
          <span className={classes.richTextSymbol}>format_list_numbered</span>
        </ButtonBase>
      </div>
      <div
        className={cx(
          classes.richTextEditor,
          isFocused && classes.richTextEditorFocused,
          error && classes.richTextEditorError,
          flash && classes.flash,
        )}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export { normalizeRichTextValue, stripRichText };
export default TiptapEditor;
